/**
 * Context catalog for Monty's proactive hints: the launcher nudge copy and
 * the "try asking" suggestions in the chat drawer, both chosen from where
 * the user currently is in the app.
 *
 * Contexts are matched in order, so the most specific route wins. Every
 * context supplies more than one nudge message: a repeat within the same
 * session rotates to the next one, so a user who ignored the first hint
 * sees something new rather than the same sentence again.
 */

export interface ChatHintContext {
  /** Stable id -- used as a React key and in tests, never shown to users. */
  key: string;
  /** Matched against `location.pathname`. */
  pattern: RegExp;
  nudgeMessages: string[];
  suggestions: string[];
}

/**
 * Ordered most-specific first. `DEFAULT_CHAT_HINT_CONTEXT` catches anything
 * that falls through, so this list never needs a catch-all entry.
 */
export const CHAT_HINT_CONTEXTS: ChatHintContext[] = [
  {
    key: 'quote-detail',
    // A single quote being built or reviewed -- /quotes/new, /quotes/new/...,
    // and /quotes/quot_xxx, but not the /quotes list root.
    pattern: /^\/quotes\/[^/]+/,
    nudgeMessages: [
      "Let's dig into this quote.",
      'Want me to sanity-check this deal?',
      'Discounts, approvals, terms — ask away.',
    ],
    suggestions: [
      'Sanity-check this quote for pricing issues',
      'Explain the discounts applied to this quote',
      "What's blocking approval on this quote?",
    ],
  },
  {
    key: 'quote-list',
    pattern: /^\/(quotes|sales\/quotes)\/?$/,
    nudgeMessages: [
      'Need a hand with your quotes?',
      'No tunnel vision here — ask me anything.',
    ],
    suggestions: [
      'Which quotes are waiting on a signature?',
      'Which quotes are waiting on my approval?',
      'Show quotes expected to close this month',
    ],
  },
  {
    key: 'billing',
    // `(?:\/|$)` keeps the match on a segment boundary -- without it
    // /billingfoo and /invoices-old would read as billing routes.
    pattern: /^\/(?:billing|invoices|bill-groups)(?:\/|$)/,
    nudgeMessages: [
      'Ask me about any invoice.',
      'I can explain that proration.',
      "Something buried in your billing? I'll find it.",
    ],
    suggestions: [
      'Explain the charges on this invoice',
      'Show unpaid invoices more than 30 days old',
      'Summarize billing health for this month in 5 bullets',
    ],
  },
  {
    key: 'account',
    pattern: /^\/accounts\/[^/]+/,
    nudgeMessages: [
      'I surface answers fast.',
      'Renewals coming up? I know which ones.',
    ],
    suggestions: [
      "Summarize this account's contract history",
      "What's this account's total ARR?",
      'Show upcoming renewals for this account',
    ],
  },
  {
    key: 'dashboard',
    // The bare `$` alternative is the app root, which lands on the dashboard.
    pattern: /^\/(?:dashboard(?:\/|$)|$)/,
    nudgeMessages: [
      'Hi! How can I help?',
      'Your revenue questions, answered.',
      'Renewals coming up? I know which ones.',
    ],
    suggestions: [
      'Show renewals coming up in the next 90 days',
      'Which quotes are waiting on a signature?',
      'Summarize billing health for this month in 5 bullets',
    ],
  },
];

export const DEFAULT_CHAT_HINT_CONTEXT: ChatHintContext = {
  key: 'default',
  pattern: /.*/,
  nudgeMessages: ['Hi! How can I help?', 'Stuck? Ask me.'],
  suggestions: [
    'Show renewals coming up in the next 90 days',
    'Which quotes are waiting on a signature?',
    'Summarize billing health for this month in 5 bullets',
  ],
};

export const getChatHintContext = (pathname: string): ChatHintContext =>
  CHAT_HINT_CONTEXTS.find(({ pattern }) => pattern.test(pathname)) ??
  DEFAULT_CHAT_HINT_CONTEXT;

/** Rotates through a context's messages so a repeat is never a repeat. */
export const getChatHintMessage = (
  context: ChatHintContext,
  shownCount: number,
): string => context.nudgeMessages[shownCount % context.nudgeMessages.length];

/**
 * Delay before the first hint of a session. Long enough for the launcher's
 * entrance bounce to land and for the landing page -- usually the dashboard
 * or a quote coming in from CRM -- to finish painting.
 */
export const CHAT_HINT_INITIAL_DELAY_MS = 4_000;

/** How long a hint stays up before quietly retiring itself. */
export const CHAT_HINT_VISIBLE_MS = 14_000;

/**
 * Gap before a second attempt, offered only when the first hint timed out
 * rather than being dismissed. Long enough that it reads as a fresh offer
 * instead of a nag.
 */
export const CHAT_HINT_REPEAT_DELAY_MS = 240_000;

/** Appearances per session, dismissal aside. Two is the whole budget. */
export const MAX_CHAT_HINTS_PER_SESSION = 2;

/**
 * How long to wait before re-checking when a modal or drawer owns the
 * screen. Landing straight into the new-quote modal from CRM is the common
 * case, and a hint competing with it is exactly the intrusion to avoid.
 */
export const CHAT_HINT_BLOCKED_RETRY_MS = 20_000;

/**
 * Give up after this many blocked attempts. Retrying forever would mean a
 * user who left a modal open all morning gets greeted the moment they close
 * it, long after the greeting made any sense.
 */
export const MAX_CHAT_HINT_BLOCKED_RETRIES = 3;
